import { useCallback, useEffect, useRef } from 'react'

import { format } from 'date-fns/esm'

import { getDefaultValue, parseField } from '../utils'

export function useActions(
  value: Record<string, uui.domain.rm.TimeWindow[]>,
  apiChange: (value: Record<string, uui.domain.rm.TimeWindow[]>) => void,
) {
  const fieldValueRef = useRef(value)

  useEffect(() => {
    fieldValueRef.current = value
  }, [value])

  const onAddException = useCallback(() => {
    // Get the default new value
    const defaultNewValue = getDefaultValue(value)

    // Parse and merge with existing value
    const parsedData = parseField([defaultNewValue])

    // Create the updated data object
    const updatedData = { ...value, ...parsedData }

    apiChange(updatedData)
  }, [apiChange, value])

  const onUpdateException = useCallback(
    (index: number, date: Date | null) => {
      if (!date) return
      const dateAsString: string = format(date, 'yyyyMMdd')

      // Get all entries as an array
      const entries = Object.entries(value)

      // Check if index is valid
      if (index < 0 || index >= entries.length) return

      //replace the date at the specified index
      entries[index] = [dateAsString, entries[index][1]]

      // Convert back to object
      const newValue = Object.fromEntries(entries)

      apiChange(newValue)
    },
    [apiChange, value],
  )

  const onRemoveException = useCallback(
    index => {
      // Get all entries as an array
      const entries = Object.entries(value)

      // Check if index is valid
      if (index < 0 || index >= entries.length) return

      // Remove the entry
      entries.splice(index, 1)

      // Convert back to object
      const newValue = Object.fromEntries(entries)

      apiChange(newValue)
    },
    [apiChange, value],
  )

  const onUpdateTimeWindows = useCallback(
    (timeWindows: [uui.domain.rm.TimeWindow?, uui.domain.rm.TimeWindow?], index: number) => {
      // Get all entries as an array
      const entries = Object.entries(value)

      // Check if index is valid
      if (index < 0 || index >= entries.length) return

      //replace the date at the specified index
      entries[index] = [entries[index][0], timeWindows as uui.domain.rm.TimeWindow[]]

      // Convert back to object
      const newValue = Object.fromEntries(entries)

      apiChange(newValue)
    },
    [apiChange, value],
  )

  return {
    onAddException,
    onUpdateException,
    onRemoveException,
    onUpdateTimeWindows,
  }
}
